// File-related API helpers + utility functions.
import { api, API } from "./api";
import {
  FileText,
  Image as ImageIcon,
  Film,
  Activity,
  Music,
  File as FileIcon,
  Star,
  Pin,
} from "lucide-react";

export const CATEGORY_LIST = [
  "Reports",
  "Imagery",
  "Footage",
  "Sensor Data",
  "Audio Intercepts",
  "Other",
];

export const CATEGORY_META = {
  Reports: { icon: FileText, color: "text-sky-400", border: "border-sky-500/30" },
  Imagery: { icon: ImageIcon, color: "text-emerald-400", border: "border-emerald-500/30" },
  Footage: { icon: Film, color: "text-amber-400", border: "border-amber-500/30" },
  "Sensor Data": { icon: Activity, color: "text-violet-400", border: "border-violet-500/30" },
  "Audio Intercepts": { icon: Music, color: "text-rose-400", border: "border-rose-500/30" },
  Other: { icon: FileIcon, color: "text-zinc-400", border: "border-zinc-700" },
};

export const formatBytes = (n) => {
  if (!n || n <= 0) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${u[i]}`;
};

export const fileDownloadUrl = (file) =>
  file?.external ? file.file_path : `${API}/files/download/${file.id}`;

// API
export const fetchFiles = (params = {}) =>
  api.get("/files", { params }).then((r) => r.data);
export const fetchFilesStats = () => api.get("/files/stats").then((r) => r.data);
export const fetchFileMeta = (id) => api.get(`/files/${id}`).then((r) => r.data);
export const startFilesSync = () => api.post("/files/sync").then((r) => r.data);
export const fetchSyncStatus = () =>
  api.get("/files/sync/status").then((r) => r.data);
export const fetchReleases = () =>
  api.get("/files/releases").then((r) => r.data);
export const syncRelease = (release, kind = "docs") =>
  api.post(`/files/sync/release`, null, { params: { release, kind } }).then((r) => r.data);
export const uploadReleaseZip = (file, { release, kind, adminToken, onUploadProgress }) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("release", release);
  fd.append("kind", kind);
  return api.post("/files/sync/upload", fd, {
    headers: {
      "X-Admin-Token": adminToken,
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress,
    timeout: 0,
  }).then((r) => r.data);
};
// Anonymous-pin tracking — when toggled while signed-out, we mirror the
// file_id in localStorage so we can `claim` them after the user signs in.
const ANON_PINS_KEY = "pursue.anon_pins";

export function getAnonPins() {
  try {
    return JSON.parse(localStorage.getItem(ANON_PINS_KEY) || "[]");
  } catch {
    return [];
  }
}

function setAnonPins(arr) {
  localStorage.setItem(ANON_PINS_KEY, JSON.stringify(Array.from(new Set(arr))));
}

export function clearAnonPins() {
  localStorage.removeItem(ANON_PINS_KEY);
}

export const togglePin = async (id) => {
  const r = await api.post(`/files/${id}/pin`).then((x) => x.data);
  // Track anon pins for later claim-on-signin. When the backend says
  // scope=='user' we don't need localStorage (already tied to the user).
  if (r.scope !== "user") {
    const cur = getAnonPins();
    if (r.pinned) setAnonPins([...cur, id]);
    else setAnonPins(cur.filter((x) => x !== id));
  }
  return r;
};
export const fetchCaseAudio = (caseId, perspective) =>
  api
    .post(`/cases/${caseId}/audio`, { perspective })
    .then((r) => r.data);

export const fetchAudioStatus = (caseId, perspective) =>
  api
    .get(`/cases/${caseId}/audio/status`, { params: { perspective } })
    .then((r) => r.data);

// Re-export icons for convenience
export { Star, Pin };
